import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { Mail, MessageSquare, MapPin, Send, Loader2, Settings, AlertCircle, CheckCircle2, WifiOff } from "lucide-react";
import { useTheme } from "@/lib/theme-context";
import { useState } from "react";
import { toast } from "sonner";
import { sendContactMessage, ContactError, type ContactErrorKind } from "@/lib/contact-config";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "اتصل بنا — روان UI" },
      { name: "description", content: "تواصل مع فريق Rawan UI لأي استفسار أو اقتراح." },
      { property: "og:title", content: "اتصل بنا — روان UI" },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "/contact" }],
  }),
  component: ContactPage,
});

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "ok" }
  | { kind: "error"; errorKind: ContactErrorKind; message: string };

function ContactPage() {
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);

  // Show the "edit recipient & template" shortcut only to admins.
  const { isAdmin: fbAdmin } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  const isAdmin = fbAdmin || localAdmin;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  function messageFor(kind: ContactErrorKind): string {
    switch (kind) {
      case "offline":
        return t("لا يوجد اتصال بالإنترنت. تحقّق من الشبكة ثم أعد المحاولة.",
                 "You appear to be offline. Check your connection and try again.",
                 "Vous semblez hors ligne. Vérifiez votre connexion et réessayez.");
      case "network":
        return t("تعذّر الاتصال بخادم الإرسال. حاول مرة أخرى بعد قليل.",
                 "Could not reach the mail server. Please try again in a moment.",
                 "Impossible de joindre le serveur de messagerie. Veuillez réessayer dans un instant.");
      case "server":
        return t("خطأ مؤقت في الخادم أثناء إرسال رسالتك. أعد المحاولة لاحقًا.",
                 "Temporary server error while sending your message. Please retry shortly.",
                 "Erreur temporaire du serveur lors de l'envoi de votre message. Veuillez réessayer sous peu.");
      case "config":
        return t("إعدادات الإرسال غير صحيحة. تواصل مع المسؤول.",
                 "The email service is misconfigured. Please contact the site admin.",
                 "Le service e-mail est mal configuré. Veuillez contacter l'administrateur du site.");
      default:
        return t("تعذّر إرسال الرسالة، حاول لاحقًا.",
                 "Could not send the message, please try again later.",
                 "Impossible d'envoyer le message, veuillez réessayer plus tard.");
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status.kind === "sending") return;
    setStatus({ kind: "sending" });
    const sendingToast = toast.loading(t("جارٍ إرسال رسالتك…", "Sending your message…", "Envoi de votre message…"));
    try {
      await sendContactMessage({ name, email, subject, message });
      setStatus({ kind: "ok" });
      setName(""); setEmail(""); setSubject(""); setMessage("");
      toast.success(
        t("تم إرسال رسالتك بنجاح ✓", "Your message was sent successfully ✓", "Votre message a été envoyé avec succès ✓"),
        { id: sendingToast, description: t("سنردّ عليك في أقرب وقت.", "We'll get back to you soon.", "Nous vous répondrons bientôt.") },
      );
      setTimeout(() => setStatus({ kind: "idle" }), 6000);
    } catch (err) {
      const kind: ContactErrorKind =
        err instanceof ContactError ? err.kind : "unknown";
      const msg = messageFor(kind);
      setStatus({ kind: "error", errorKind: kind, message: msg });
      toast.error(
        t("تعذّر إرسال الرسالة", "Could not send the message", "Impossible d'envoyer le message"),
        { id: sendingToast, description: msg },
      );
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-10 max-w-5xl">
        <header className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold">{t("اتصل بنا", "Contact Us", "Contactez-nous")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("نسعد بسماع رأيك واقتراحاتك", "We'd love to hear from you", "Nous serions ravis d'avoir de vos nouvelles")}
          </p>
          {isAdmin ? (
            <Link
              to="/admin/contact"
              className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-lg bg-card border border-primary/40 text-primary hover:bg-primary/10 text-sm"
            >
              <Settings className="h-4 w-4" />
              {t("إعدادات نموذج الاتصال", "Contact form settings", "Paramètres du formulaire de contact")}
            </Link>
          ) : null}
        </header>

        <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
          {[
            { Icon: Mail,           ar: "البريد", en: "Email",    fr: "E-mail",       v: "contact@elhizazi.me" },
            { Icon: MessageSquare,  ar: "الدعم",  en: "Support",  fr: "Support",      v: "support@elhizazi.me" },
            { Icon: MapPin,         ar: "الموقع", en: "Location", fr: "Emplacement",  v: t("عبر الإنترنت — عالميًا", "Online — Worldwide", "En ligne — Mondial") },
          ].map(({ Icon, ar, en, fr, v }) => (
            <div key={en} className="bg-card border border-border rounded-xl sm:rounded-2xl p-2.5 sm:p-5 text-center hover:shadow-glow transition min-w-0">
              <div className="grid place-items-center w-8 h-8 sm:w-12 sm:h-12 mx-auto rounded-lg sm:rounded-xl mb-2 sm:mb-3" style={{ background: "var(--gradient-primary)" }}>
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
              </div>
              <h3 className="font-bold text-xs sm:text-base">{t(ar, en, fr)}</h3>
              <p className="text-[10px] sm:text-sm text-muted-foreground mt-1 leading-snug break-words">{v}</p>
            </div>
          ))}
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-10 bg-card border border-border rounded-2xl p-6 md:p-8 grid gap-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("الاسم الكامل", "Full name", "Nom complet")}
              className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a", "Email", "E-mail")}
              className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
          </div>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("\u0627\u0644\u0645\u0648\u0636\u0648\u0639", "Subject", "Sujet")}
            className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition"
          />
          <textarea
            required
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("رسالتك…", "Your message…", "Votre message…")}
            className="px-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary transition resize-none"
          />

          {status.kind === "ok" ? (
            <div className="flex items-center gap-2 text-sm text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
              <CheckCircle2 className="h-4 w-4" />
              {t("تم إرسال رسالتك بنجاح، شكرًا لك!",
                 "Your message was sent successfully, thank you!",
                 "Votre message a été envoyé avec succès, merci !")}
            </div>
          ) : null}
          {status.kind === "error" ? (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
              {status.errorKind === "offline" ? (
                <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              )}
              <div className="flex-1">
                <div>{status.message}</div>
                <button
                  type="button"
                  onClick={() => onSubmit(new Event("submit") as unknown as React.FormEvent)}
                  className="mt-1 text-xs underline opacity-80 hover:opacity-100"
                >
                  {t("إعادة المحاولة", "Try again", "Réessayer")}
                </button>
              </div>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={status.kind === "sending"}
            className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-60"
          >
            {status.kind === "sending" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {status.kind === "sending"
              ? t("جارٍ الإرسال…", "Sending…", "Envoi…")
              : t("إرسال الرسالة", "Send Message", "Envoyer le message")}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
